import { useCallback, useEffect, useMemo, useState } from "react";
import { api, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Network, Building2, MapPin, Users, ChevronDown, ChevronRight,
  Search, RefreshCw, Layers, X,
} from "lucide-react";

const LEVEL_LABELS = {
  1: "Holding",
  2: "Pháp nhân",
  3: "Cluster",
  4: "Site",
};

const LEVEL_TONE = {
  1: { bg: "bg-emerald-900", text: "text-white", chip: "bg-emerald-50 text-emerald-900 border-emerald-200" },
  2: { bg: "bg-emerald-700", text: "text-white", chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  3: { bg: "bg-amber-50 border border-amber-300", text: "text-stone-900", chip: "bg-amber-50 text-amber-800 border-amber-200" },
  4: { bg: "bg-white border border-stone-300", text: "text-stone-900", chip: "bg-stone-50 text-stone-700 border-stone-200" },
};

export default function OrgChart() {
  const [units, setUnits] = useState([]);
  const [headcount, setHeadcount] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(() => new Set([])); // collapsed by default after init
  const [autoExpanded, setAutoExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [u, h] = await Promise.all([
        api.get("/org/units"),
        api.get("/org/headcount").catch(() => ({ data: [] })),
      ]);
      setUnits(u.data);
      setHeadcount(h.data);
    } catch (e) {
      setError(parseApiError(e, "Không tải được sơ đồ tổ chức").message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Build tree
  const { roots, byParent, headByCompany, headByBranch, deptByBranch } = useMemo(() => {
    const _byParent = new Map();
    const _roots = [];
    units.forEach((u) => {
      if (u.parent_id) {
        if (!_byParent.has(u.parent_id)) _byParent.set(u.parent_id, []);
        _byParent.get(u.parent_id).push(u);
      } else {
        _roots.push(u);
      }
    });
    _roots.sort((a, b) => a.level - b.level || a.code.localeCompare(b.code));
    _byParent.forEach((v) => v.sort((a, b) => a.code.localeCompare(b.code)));

    // Headcount summaries
    const _hc = new Map();
    const _hb = new Map();
    const _db = new Map();
    headcount.forEach((h) => {
      const c = h.company_id;
      const b = h.branch_id;
      const cnt = Number(h.headcount) || 0;
      if (c) _hc.set(c, (_hc.get(c) || 0) + cnt);
      if (b) _hb.set(b, (_hb.get(b) || 0) + cnt);
      if (b && h.department) {
        if (!_db.has(b)) _db.set(b, new Map());
        const sub = _db.get(b);
        sub.set(h.department, (sub.get(h.department) || 0) + cnt);
      }
    });

    return { roots: _roots, byParent: _byParent, headByCompany: _hc, headByBranch: _hb, deptByBranch: _db };
  }, [units, headcount]);

  // Auto-expand all on first load
  useEffect(() => {
    if (!autoExpanded && units.length > 0) {
      const all = new Set(units.map((u) => u.id));
      setExpanded(all);
      setAutoExpanded(true);
    }
  }, [units, autoExpanded]);

  const filterPredicate = (u) => {
    if (!q.trim()) return true;
    const k = q.trim().toLowerCase();
    return u.name?.toLowerCase().includes(k) || u.code?.toLowerCase().includes(k);
  };

  const hasMatchRec = (u) => {
    if (filterPredicate(u)) return true;
    const children = byParent.get(u.id) || [];
    return children.some(hasMatchRec);
  };

  const totalEmps = useMemo(
    () => headcount.reduce((sum, h) => sum + (Number(h.headcount) || 0), 0),
    [headcount],
  );

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpanded(new Set(units.map((u) => u.id)));
  const collapseAll = () => setExpanded(new Set());

  const headForUnit = (u) => {
    if (u.level <= 2 && u.company_id) return headByCompany.get(u.company_id) || 0;
    if (u.branch_id) return headByBranch.get(u.branch_id) || 0;
    return null;
  };

  const renderUnit = (u, depth = 0) => {
    if (!hasMatchRec(u)) return null;
    const children = byParent.get(u.id) || [];
    const isOpen = expanded.has(u.id);
    const tone = LEVEL_TONE[u.level] || LEVEL_TONE[4];
    const head = headForUnit(u);
    const depts = u.branch_id ? deptByBranch.get(u.branch_id) : null;

    return (
      <div key={u.id} style={{ marginLeft: depth === 0 ? 0 : 24 }} className="relative">
        {depth > 0 && (
          <span
            className="absolute -left-3 top-0 bottom-0 border-l border-stone-300"
            aria-hidden
          />
        )}
        <div className="flex items-start gap-2 my-1.5">
          {children.length > 0 ? (
            <button
              onClick={() => toggleExpand(u.id)}
              className="mt-2 flex-shrink-0 w-5 h-5 rounded-sm border border-stone-300 bg-white text-stone-600 hover:bg-stone-100 flex items-center justify-center"
            >
              {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </button>
          ) : (
            <div className="w-5 h-5 mt-2 flex-shrink-0" />
          )}
          <div
            className={`flex-1 ${tone.bg} ${tone.text} rounded-sm px-3 py-2 flex items-center gap-3 transition-shadow hover:shadow-sm`}
          >
            <span
              className={`text-[9px] uppercase tracking-wider font-bold border px-1.5 py-0.5 rounded-sm ${tone.chip}`}
            >
              L{u.level} · {LEVEL_LABELS[u.level] || ""}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{u.name}</div>
              <div className={`text-[11px] font-mono ${u.level <= 2 ? "text-white/70" : "text-stone-500"}`}>
                {u.code}
                {u.division_code && ` · ${u.division_code}`}
                {u.unit_type && ` · ${u.unit_type}`}
              </div>
            </div>
            {head != null && (
              <div className="text-right flex-shrink-0 flex items-center gap-1">
                <Users className={`w-3.5 h-3.5 ${u.level <= 2 ? "text-white/70" : "text-stone-500"}`} />
                <span className="font-mono text-sm font-bold">{head}</span>
              </div>
            )}
          </div>
        </div>

        {/* Departments under this unit (only show for branches/sites) */}
        {depts && depts.size > 0 && isOpen && (
          <div className="ml-7 mb-2 flex flex-wrap gap-1">
            {Array.from(depts.entries())
              .sort((a, b) => b[1] - a[1])
              .map(([name, count]) => (
                <span
                  key={name}
                  className="inline-flex items-center gap-1 text-[10px] bg-stone-100 text-stone-700 border border-stone-200 rounded-sm px-1.5 py-0.5"
                >
                  <Layers className="w-3 h-3 text-stone-400" />
                  {name} <strong className="font-mono text-stone-900">{count}</strong>
                </span>
              ))}
          </div>
        )}

        {isOpen && children.length > 0 && (
          <div className="ml-3">{children.map((c) => renderUnit(c, depth + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="page-org-chart">
      <PageHeader
        kicker="Tổ chức"
        title="Sơ đồ tổ chức"
        breadcrumb={["Tổ chức", "Sơ đồ tổ chức"]}
        actions={
          <>
            <Button variant="outline" onClick={load} className="rounded-sm">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Tải lại
            </Button>
            <Button variant="outline" onClick={expandAll} className="rounded-sm">
              Mở rộng
            </Button>
            <Button variant="outline" onClick={collapseAll} className="rounded-sm">
              Thu gọn
            </Button>
          </>
        }
      />

      <div className="p-6 lg:p-8 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Đơn vị tổ chức" value={units.length} sub="L1 → L4" tone="emerald" icon={<Network className="w-4 h-4" />} />
          <StatCard
            label="Pháp nhân"
            value={units.filter((u) => u.level === 2).length}
            sub="Công ty / chi nhánh độc lập"
            tone="sky"
            icon={<Building2 className="w-4 h-4" />}
          />
          <StatCard
            label="Site / Outlet"
            value={units.filter((u) => u.level === 4).length}
            sub="Cấp vận hành"
            tone="amber"
            icon={<MapPin className="w-4 h-4" />}
          />
          <StatCard label="Tổng nhân sự" value={totalEmps} sub="Đang làm việc" tone="stone" icon={<Users className="w-4 h-4" />} />
        </div>

        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                placeholder="Tìm đơn vị theo tên / mã..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-8 h-9 rounded-sm"
                data-testid="org-search"
              />
            </div>
            {q && (
              <Button variant="ghost" size="sm" onClick={() => setQ("")} className="rounded-sm h-9 text-xs">
                <X className="w-3.5 h-3.5 mr-1" /> Xóa
              </Button>
            )}
            <div className="ml-auto text-xs text-stone-500 font-mono">{units.length} đơn vị</div>
          </div>

          <div className="p-4 lg:p-6">
            {loading && <div className="text-center text-stone-500 py-8">Đang tải sơ đồ...</div>}
            {error && !loading && (
              <div className="text-center py-8">
                <div className="text-red-700 text-sm mb-2">{error}</div>
                <Button size="sm" variant="outline" onClick={load} className="rounded-sm">
                  Thử lại
                </Button>
              </div>
            )}
            {!loading && !error && roots.length === 0 && (
              <div className="text-center py-8 text-stone-500">
                <Network className="w-8 h-8 mx-auto text-stone-300 mb-2" />
                Chưa có đơn vị tổ chức nào.
                <div className="text-xs mt-1">
                  Vào trang <a href="/admin/companies" className="text-emerald-800 hover:underline font-semibold">Pháp nhân</a> để khởi tạo.
                </div>
              </div>
            )}
            {!loading && !error && roots.length > 0 && (
              <div className="space-y-1">{roots.map((r) => renderUnit(r, 0))}</div>
            )}
          </div>
        </div>

        <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-xs text-stone-600 flex items-start gap-2">
          <Layers className="w-4 h-4 text-stone-400 mt-0.5" />
          <div>
            <strong className="text-stone-800">Cấu trúc 4 tầng:</strong> L1 Holding · L2 Pháp nhân · L3 Cluster ·
            L4 Site. Headcount tính theo nhân viên đang làm việc (chưa nghỉ).
            Có thể thêm / sửa đơn vị tổ chức ở trang{" "}
            <a href="/admin/companies" className="text-emerald-800 font-semibold hover:underline">
              Pháp nhân & Chi nhánh
            </a>
            .
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sub, tone }) {
  const toneClass =
    tone === "emerald"
      ? "border-l-emerald-600"
      : tone === "sky"
      ? "border-l-sky-600"
      : tone === "amber"
      ? "border-l-amber-500"
      : "border-l-stone-400";
  const iconBg =
    tone === "emerald"
      ? "bg-emerald-50 text-emerald-700"
      : tone === "sky"
      ? "bg-sky-50 text-sky-700"
      : tone === "amber"
      ? "bg-amber-50 text-amber-700"
      : "bg-stone-100 text-stone-700";
  return (
    <div className={`bg-white border border-stone-200 border-l-4 ${toneClass} rounded-sm px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-bold">{label}</div>
        <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${iconBg}`}>{icon}</div>
      </div>
      <div className="font-serif text-3xl font-bold text-stone-900 mt-1 tracking-tight">{value}</div>
      <div className="text-[11px] text-stone-500 mt-0.5">{sub}</div>
    </div>
  );
}
